import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { Info, Pencil } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'
import { Alert, AlertDescription, AlertTitle } from '@/common/ui/alert'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Switch } from '@/common/ui/switch'
import showErrorToast from '@/common/utils/showErrorToast'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { CouponType } from '@/schema'
import useMutationUpdateBatch from '../../api/hooks/useMutationUpdateBatch'
import { BatchesQueryKeys } from '../../keys'
import { GetBatchesDataResponse } from '../../type'

const capacitySchema = z.object({
	name: z.string().min(1, 'กรุณากรอกชื่อชุดคูปอง'),
	description: z.string().optional(),
	isUnlimited: z.boolean(),
	totalCapacity: z.number().nullable().optional(),
})

type CapacityFormValues = z.infer<typeof capacitySchema>

interface UpdateBatchCapacityDialogProps {
	batch: GetBatchesDataResponse
	trigger?: React.ReactNode
}

export default function UpdateBatchCapacityDialog({
	batch,
	trigger,
}: UpdateBatchCapacityDialogProps) {
	const [open, setOpen] = useState(false)
	const updateBatch = useMutationUpdateBatch()
	const queryClient = useQueryClient()
	const { data: user } = useQueryProfile()
	const isSingleType = batch.type === CouponType.SINGLE
	const usedCount = isSingleType ? batch.usedQuota : batch.usedCoupons
	const currentCapacity = isSingleType ? batch.maxQuota : batch.totalCoupons

	const form = useForm<CapacityFormValues>({
		resolver: zodResolver(capacitySchema),
		defaultValues: {
			name: batch.name,
			description: batch.description ?? '',
			isUnlimited: isSingleType && batch.maxQuota == null,
			totalCapacity: currentCapacity ?? null,
		},
	})

	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_COUPON,
	)

	if (!hasWritePermission) return null

	const defaultTrigger = (
		<Button variant="ghost" size="icon">
			<Pencil className="h-4 w-4" />
		</Button>
	)

	const handleSubmit = async (values: CapacityFormValues) => {
		const nextCapacity = values.totalCapacity

		if (!isSingleType || !values.isUnlimited) {
			if (!nextCapacity) {
				form.setError('totalCapacity', {
					message: 'กรุณากรอกจำนวนรวม',
				})
				return
			}

			if (nextCapacity < usedCount) {
				form.setError('totalCapacity', {
					message: `จำนวนรวมต้องไม่น้อยกว่าจำนวนที่ใช้ไปแล้ว (${usedCount})`,
				})
				return
			}
		}

		try {
			await updateBatch.mutateAsync({
				id: batch.id,
				request: {
					name: values.name,
					description: values.description ?? '',
					...(isSingleType
						? { maxQuota: values.isUnlimited ? null : nextCapacity }
						: { totalCoupons: nextCapacity ?? undefined }),
				},
			})

			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: [BatchesQueryKeys.GetBatches],
				}),
				queryClient.invalidateQueries({
					queryKey: [BatchesQueryKeys.GetCoupons],
				}),
			])

			setOpen(false)
			toast.success('แก้ไขชุดคูปองสำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<>
			<div onClick={() => setOpen(true)}>{trigger || defaultTrigger}</div>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>แก้ไขชุดคูปอง</DialogTitle>
					</DialogHeader>

					<Alert className="border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100 [&>svg]:text-amber-500">
						<Info className="h-4 w-4" />
						<AlertTitle className="text-amber-900 dark:text-amber-100">
							ข้อมูลที่สามารถแก้ไขได้
						</AlertTitle>
						<AlertDescription className="text-amber-800 dark:text-amber-200">
							แก้ไขได้เฉพาะชื่อชุดคูปอง รายละเอียด และจำนวน/โควตาคูปอง เท่านั้น
							ไม่สามารถแก้ไขประเภทรางวัล โปรโมชั่น จำนวนเครดิตฟรี ประเภทคูปอง
							หรือ Prefix ได้ หากต้องการเปลี่ยนข้อมูลเหล่านี้
							กรุณาสร้างชุดคูปองใหม่
						</AlertDescription>
					</Alert>

					<div className="rounded-lg border bg-muted/40 p-3 text-sm">
						<div>ใช้ไปแล้ว: {usedCount.toLocaleString('th-TH')}</div>
						{isSingleType ? (
							<div>
								โควตาปัจจุบัน:{' '}
								{batch.maxQuota == null
									? 'ไม่จำกัด'
									: batch.maxQuota.toLocaleString('th-TH')}
							</div>
						) : (
							<>
								<div>
									จำนวนรวมปัจจุบัน: {batch.totalCoupons.toLocaleString('th-TH')}
								</div>
								<div>
									คงเหลือ: {batch.remainingCoupons.toLocaleString('th-TH')}
								</div>
							</>
						)}
					</div>

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="space-y-4"
						>
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ชื่อชุดคูปอง</FormLabel>
										<FormControl>
											<Input placeholder="กรุณากรอกชื่อชุดคูปอง" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>รายละเอียด</FormLabel>
										<FormControl>
											<Input
												placeholder="กรุณากรอกรายละเอียด (ไม่บังคับ)"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{isSingleType && (
								<FormField
									control={form.control}
									name="isUnlimited"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
											<div className="space-y-0.5">
												<FormLabel>ไม่จำกัดจำนวนครั้ง</FormLabel>
												<FormDescription>
													เปิดไว้เพื่อให้คูปองเดี่ยวใช้ได้ไม่จำกัดจำนวนครั้ง
												</FormDescription>
											</div>
											<FormControl>
												<Switch
													checked={field.value}
													onCheckedChange={(checked) => {
														field.onChange(checked)
														if (checked) {
															form.clearErrors('totalCapacity')
														}
													}}
												/>
											</FormControl>
										</FormItem>
									)}
								/>
							)}

							{(!isSingleType || !form.watch('isUnlimited')) && (
								<FormField
									control={form.control}
									name="totalCapacity"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{isSingleType ? 'โควตารวมใหม่' : 'จำนวนคูปองรวมใหม่'}
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													min={Math.max(usedCount, 1)}
													placeholder={`ขั้นต่ำ ${Math.max(usedCount, 1).toLocaleString('th-TH')}`}
													value={field.value ?? ''}
													onChange={(event) =>
														field.onChange(
															event.target.value
																? Number(event.target.value)
																: null,
														)
													}
												/>
											</FormControl>
											<FormDescription>
												ลดได้เฉพาะส่วนที่ยังไม่ถูกใช้เท่านั้น
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}

							<div className="flex justify-end gap-3 pt-2">
								<Button
									type="button"
									variant="outline"
									onClick={() => setOpen(false)}
								>
									ยกเลิก
								</Button>
								<Button type="submit" disabled={updateBatch.isPending}>
									{updateBatch.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
								</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</>
	)
}
